import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { grantLicense, revokeLicense } from "../api/licenses";
import { ApiError } from "../../api/errors";
import { useHostToast } from "../../host/useHostToast";
import type { MemberLicense } from "../types";

// AHA-43204: shape returned by the backend when grant hits the licence cap.
// Backend throws HttpException({ error: "cap_exceeded", licenseQuantity, count })
// from licenses.service.ts. We only key off the error code; the numbers are
// best-effort context for future logging if we ever want it.
function isCapExceededError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status !== 400) return false;
  const body = err.body as { error?: string } | null;
  return body?.error === "cap_exceeded";
}

interface UpdateResult {
  ok: boolean;
  /** The new license row on grant, null on revoke. Lets the caller patch
   *  the member in place instead of refetching the whole list. */
  license: MemberLicense | null;
}

interface UseUpdateMemberLicense {
  /** Member id whose license change is in flight — drives the per-row
   *  spinner, mirroring useUpdateMemberRole.pendingMemberId. */
  pendingMemberId: string | null;
  update: (userId: string, licensed: boolean) => Promise<UpdateResult>;
}

export function useUpdateMemberLicense(): UseUpdateMemberLicense {
  const { t } = useTranslation();
  const toast = useHostToast();
  const [pendingMemberId, setPendingMemberId] = useState<string | null>(null);

  const update = useCallback(
    async (userId: string, licensed: boolean): Promise<UpdateResult> => {
      setPendingMemberId(userId);
      try {
        if (licensed) {
          const license = await grantLicense(userId);
          toast.success(t("team.toast.licenseGranted"));
          return { ok: true, license };
        }
        await revokeLicense(userId);
        toast.success(t("team.toast.licenseRevoked"));
        return { ok: true, license: null };
      } catch (err) {
        // Specific cap-exceeded toast (AHA-43204) for the race case where
        // the FE thought there were seats but a concurrent grant beat us
        // to the last one. The Grant menu item is also gated on
        // canGrantMore in MemberLicenseSelection so this rarely fires.
        const key = isCapExceededError(err)
          ? "team.toast.licenseCapExceeded"
          : "team.toast.licenseUpdateFailed";
        toast.error(t(key));
        return { ok: false, license: null };
      } finally {
        setPendingMemberId(null);
      }
    },
    [t, toast],
  );

  return { update, pendingMemberId };
}
